import { AgentProfileEditor } from "@/components/AgentProfileEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { AIService } from "@/gen/omnios/v1/ai_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Search, Trash2 } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/settings/agent-profiles")({
	component: AgentProfilesPage,
});

const aiClient = createClient(AIService, transport);

function AgentProfilesPage() {
	const { t } = useTranslation("settings");
	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: "Профили агентов" },
	]);

	const qc = useQueryClient();
	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const clearSearch = useCallback(() => setSearch(""), []);
	useSlashFocus(searchRef, clearSearch);
	const [showEditor, setShowEditor] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["agent-profiles"],
		queryFn: async () => {
			const res = await aiClient.listAgentProfiles({
				includeSystemTemplates: true,
			});
			return res.profiles;
		},
	});

	const deleteMut = useMutation({
		mutationFn: (id: string) => aiClient.deleteAgentProfile({ id }),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["agent-profiles"] });
			toast.success("Профиль удалён");
		},
	});

	const searchLower = search.toLowerCase();
	const profiles = search
		? (data ?? []).filter((p) => p.name.toLowerCase().includes(searchLower))
		: (data ?? []);

	return (
		<div className="space-y-4">
			{/* Toolbar */}
			<div className="flex items-center gap-3">
				<div className="relative flex-1 max-w-sm">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						ref={searchRef}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Поиск профилей..."
						className="pl-9 h-9"
					/>
				</div>
				<div className="ml-auto">
					<Button size="sm" onClick={() => setShowEditor(true)}>
						<Plus className="h-3.5 w-3.5 mr-1.5" />
						Создать
					</Button>
				</div>
			</div>

			{/* Table */}
			{isLoading ? (
				<div className="flex justify-center py-12">
					<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
				</div>
			) : profiles.length === 0 ? (
				<div className="text-center py-12 text-sm text-muted-foreground">
					{search ? "Ничего не найдено" : "Нет профилей"}
				</div>
			) : (
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Название</TableHead>
							<TableHead>Модель</TableHead>
							<TableHead>Режим</TableHead>
							<TableHead className="w-[100px]">Тип</TableHead>
							<TableHead className="w-[50px]" />
						</TableRow>
					</TableHeader>
					<TableBody>
						{profiles.map((p) => (
							<TableRow key={p.id}>
								<TableCell>
									<div>
										<span className="font-medium text-sm">{p.name}</span>
										{p.systemPrompt && (
											<p className="text-xs text-muted-foreground truncate max-w-[300px]">
												{p.systemPrompt.slice(0, 80)}
											</p>
										)}
									</div>
								</TableCell>
								<TableCell className="text-sm text-muted-foreground">
									{p.defaultModel || "default"}
								</TableCell>
								<TableCell className="text-sm text-muted-foreground">
									{p.runMode || "auto"}
								</TableCell>
								<TableCell>
									{p.isTemplate ? (
										<Badge variant="secondary" className="text-[10px]">
											системный
										</Badge>
									) : (
										<Badge variant="outline" className="text-[10px]">
											свой
										</Badge>
									)}
								</TableCell>
								<TableCell>
									{!p.isTemplate && (
										<Button
											variant="ghost"
											size="icon"
											className="h-8 w-8 text-muted-foreground hover:text-destructive"
											onClick={() => deleteMut.mutate(p.id)}
										>
											<Trash2 className="h-3.5 w-3.5" />
										</Button>
									)}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}

			<AgentProfileEditor
				open={showEditor}
				onClose={() => setShowEditor(false)}
			/>
		</div>
	);
}
